import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Tag, Typography, Table, Input, Space, Dropdown, Button, App, Tooltip } from 'antd';
import { MoreOutlined, EyeOutlined, EditOutlined, KeyOutlined, LockOutlined, DeleteOutlined, MailOutlined, SafetyOutlined, PlusOutlined } from '@ant-design/icons';
import { listUsers, updateUserStatus, deleteUser, resendActivation,sendTemporaryPassword } from '../../services/users';
import { ROLES_OPTIONS, getRoleLabel } from '../../utils/constants';
const { Title } = Typography;
const { useApp } = App;

const UsersList = () => {
  const navigate = useNavigate();
  const { message, modal } = useApp();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [sorter, setSorter] = useState({});
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async (page, pageSize, srt, q) => {
    setLoading(true);
    try {
      const { items } = await listUsers({
        page: page || pagination.current,
        pageSize: pageSize || pagination.pageSize,
        search: q || undefined,
        sortField: srt?.field,
        sortOrder: srt?.order,
      });
      setData(items.data || []);
      setPagination(prev => ({ 
        ...prev, 
        current: page || prev.current, 
        pageSize: pageSize || prev.pageSize, 
        total: items.total 
      }));
    } catch (err) {
      console.error(err);
      message.error('No se pudo cargar la lista de usuarios.');
    } finally {
      setLoading(false);
    }
  }, [pagination, message]);

  useEffect(() => {
    fetchData(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (pag, filters, srt) => {
    setSorter(srt || {});
    fetchData(pag.current, pag.pageSize, srt, search);
  };

  const onSearch = (value) => {
    setSearch(value || '');
    // al buscar, reinicia a página 1
    fetchData(1, pagination.pageSize, sorter, value || '');
  };


      /**
   * Función para enviar contraseña temporal
   */
      const handleSendTemporaryPassword = async (record) => {
        modal.confirm({
          title: '¿Enviar contraseña temporal?',
          content: (
            <div>
              <p>Se enviará una contraseña temporal a:</p>
              <p style={{ fontWeight: 'bold' }}>{record.email}</p>
              <p style={{ color: '#fa8c16', marginTop: 8, fontSize: '12px' }}>
                ⚠️ El usuario recibirá un correo con la nueva contraseña
              </p>
            </div>
          ),
          okText: 'Enviar',
          cancelText: 'Cancelar',
          okButtonProps: {
            icon: <KeyOutlined />,
          },
          onOk: async () => {
            try {
      
              
              // Llamar al servicio con el email del usuario
              await sendTemporaryPassword({ email: record.email });
              
              message.success(`Contraseña temporal enviada a ${record.email}`);
              
              // Opcional: Refrescar datos si es necesario
              if (fetchData) {
                fetchData(pagination.current, pagination.pageSize, sorter, search);
              }
              
            } catch (error) {
              console.error('Error enviando contraseña temporal:', error);
              
              const errorMessage = error?.response?.data?.message 
                || error?.message 
                || 'Error al enviar la contraseña temporal';
              
              message.error(errorMessage);
            } finally {
            }
          },
        });
      };
  
  // Función para manejar las acciones del menú
  const handleMenuAction = (action, record) => {
    console.log('Acción:', action, 'Usuario:', record);
    
    switch (action) {
      case 'view':
        navigate(`/users/view/${record.id}`);
        break;
      case 'edit':
        navigate(`/users/${record.id}/edit`);
        break;
      case 'changePassword':
        handleSendTemporaryPassword(record);
        break;
      case 'toggle':
        const willActivate = !record.isActive;
        modal.confirm({
          title: willActivate ? '¿Activar usuario?' : '¿Desactivar usuario?',
          content: `${record.name || record.email || 'Usuario'}`,
          okText: willActivate ? 'Activar' : 'Desactivar',
          cancelText: 'Cancelar',
          onOk: async () => {
            try {
              await updateUserStatus(record.id, willActivate);
              message.success(willActivate ? 'Usuario activado' : 'Usuario desactivado');
              fetchData(pagination.current, pagination.pageSize, sorter, search);
            } catch {
              message.error('No se pudo actualizar el estatus');
            }
          },
        });
        break;
      case 'delete':
        modal.confirm({
          title: '¿Eliminar usuario?',
          content: `${record.name || record.email || 'Usuario'}`,
          okText: 'Eliminar',
          okButtonProps: { danger: true },
          cancelText: 'Cancelar',
          onOk: async () => {
            try {
              await deleteUser(record.id);
              message.success('Usuario eliminado');

              const nextPage = (data.length === 1 && pagination.current > 1)
                ? pagination.current - 1
                : pagination.current;
              setPagination(prev => ({ ...prev, current: nextPage }));
              fetchData(nextPage, pagination.pageSize, sorter, search);
            } catch {
              message.error('No se pudo eliminar el usuario');
            }
          },
        });
        break;
      case 'resendInvitation':
        modal.confirm({
          title: '¿Reenviar invitación?',
          content: `Se enviará un correo de activación a ${record.email || 'el usuario'}`,
          okText: 'Reenviar',
          cancelText: 'Cancelar',
          onOk: async () => {
            try {
              await resendActivation(record.id);
              message.success('Invitación reenviada correctamente');
            } catch {
              message.error('No se pudo reenviar la invitación');
            }
          },
        });
        break;
      default:
        break;
    }
  };

  const getActionsMenu = (record) => {
    const menuItems = [
      {
        key: 'view',
        icon: <EyeOutlined />,
        label: 'Ver usuario',
      },
      {
        key: 'edit',
        icon: <EditOutlined />,
        label: 'Editar usuario',
      },
      {
        key: 'changePassword',
        icon: <KeyOutlined />,
        label: 'Cambiar contraseña',
      },
      {
        type: 'divider',
      },
      {
        key: 'toggle',
        icon: <LockOutlined />,
        label: record.isActive ? 'Desactivar' : 'Activar',
      },
      {
        key: 'delete',
        icon: <DeleteOutlined />,
        label: 'Borrar usuario',
        danger: true,
      }
    ];

    return {
      items: menuItems,
      onClick: ({ key }) => handleMenuAction(key, record),
    };
  };

  const columns = [
    { 
      title: 'Nombre Usuario', 
      dataIndex: 'fullName', 
      key: 'fullName', 
      sorter: true,
      render: (_, record) => {
        const fullName = [record.firstName, record.lastName, record.secondLastName]
          .filter(Boolean)
          .join(' ');
        return fullName || 'Sin nombre';
      }
    },
    { title: 'Email', dataIndex: 'email', key: 'email', sorter: true },
    { 
      title: 'Rol', 
      dataIndex: 'role', 
      key: 'role',
      render: (role) => getRoleLabel(role)
    },
    {
      title: 'Estatus',
      dataIndex: 'isActive',
      key: 'status',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'volcano'}>
          {isActive ? 'Activo' : 'Inactivo'}
        </Tag>
      ),
      // filters: [
      //   { text: 'Activo', value: '1' },
      //   { text: 'Inactivo', value: '0' },
      // ],
      // onFilter: (value, record) => String(record.is_active ? 1 : 0) === String(value),
    },
    {
      title: 'Acciones',
      key: 'actions',
      align: 'center',
      width: 100,
      render: (_, record) => (
        <Dropdown menu={getActionsMenu(record)} trigger={['click']} placement="bottomRight">
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={(e) => e.stopPropagation()}
            style={{ fontSize: '16px' }}
          />
        </Dropdown>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={<Title level={3} style={{ marginBottom: 0 }}>Usuarios</Title>}
        extra={
          <Tooltip title="Agregar usuario">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              aria-label="Agregar usuario"
              onClick={() => navigate('/users/add')}
            />
          </Tooltip>
        }
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input.Search
            placeholder="Buscar por nombre o email"
            allowClear
            onSearch={onSearch}
            style={{ maxWidth: 360 }}
          />
          
          <Table
            rowKey={(r) => r.id ?? r._id}
            columns={columns}
            dataSource={data}
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
            }}
            onChange={onChange}
          />
        </Space>
      </Card>
    </div>
  );
};

export default UsersList;
